import React, { useState, useEffect } from 'react';
import { api } from '../../../lib/api';
import { useOrg } from '../../../hooks/useOrg';

// TIME comes back from Postgres as 'HH:MM:SS'; <input type="time"> wants 'HH:MM'
function toInputTime(dbTime) {
  if (!dbTime) return '';
  return String(dbTime).slice(0, 5);
}

function toDbTime(inputTime) {
  if (!inputTime) return null;
  return inputTime.length === 5 ? `${inputTime}:00` : inputTime;
}

export default function RemindersTab() {
  const { currentOrg, refetchMemberships } = useOrg();

  const [nightEnabled, setNightEnabled] = useState(false);
  const [nightTime, setNightTime] = useState('20:00');
  const [morningEnabled, setMorningEnabled] = useState(false);
  const [morningTime, setMorningTime] = useState('08:00');

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentOrg) return;
    const nb = currentOrg.reminder_night_before_time;
    const mo = currentOrg.reminder_morning_of_time;
    setNightEnabled(!!nb);
    setNightTime(nb ? toInputTime(nb) : '20:00');
    setMorningEnabled(!!mo);
    setMorningTime(mo ? toInputTime(mo) : '08:00');
  }, [currentOrg]);

  const handleSave = async () => {
    if (!currentOrg) return;
    setSaving(true);
    setError('');
    try {
      await api.updateOrg(currentOrg.id, {
        reminder_night_before_time: nightEnabled ? toDbTime(nightTime) : null,
        reminder_morning_of_time: morningEnabled ? toDbTime(morningTime) : null,
      });
      setSavedAt(new Date());
      if (refetchMemberships) await refetchMemberships();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'px-3.5 py-2.5 bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div>
      <div className="mb-5">
        <h3 className="text-[15px] font-bold text-primary-c">Appointment Reminders</h3>
        <p className="text-[12px] text-muted-c mt-1">
          Automated email reminders sent to patients before their appointment. Times are in each patient's local timezone.
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <ReminderRow
          title="Night before"
          description="Sent the evening before the appointment"
          enabled={nightEnabled}
          onToggle={setNightEnabled}
          time={nightTime}
          onTimeChange={setNightTime}
          inputClass={inputClass}
        />
        <ReminderRow
          title="Morning of"
          description="Sent the morning of the appointment"
          enabled={morningEnabled}
          onToggle={setMorningEnabled}
          time={morningTime}
          onTimeChange={setMorningTime}
          inputClass={inputClass}
        />
      </div>

      <div className="flex items-center justify-between mt-6">
        <div className="text-[11px] text-muted-c">
          {savedAt && `Saved ${savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all disabled:opacity-50"
        >
          {saving ? 'Saving\u2026' : 'Save Reminders'}
        </button>
      </div>

      <p className="text-[11px] text-muted-c mt-6 leading-relaxed">
        Reminders are sent within a 30-minute window of the configured time and only once per appointment per kind.
        Patients in different time zones receive the reminder at <em>their</em> local time.
      </p>
    </div>
  );
}

function ReminderRow({ title, description, enabled, onToggle, time, onTimeChange, inputClass }) {
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${enabled ? 'bg-elevated border-theme' : 'bg-tertiary border-theme opacity-70'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onToggle(!enabled)}
        className={`relative w-10 h-6 rounded-full transition-all flex-shrink-0 ${enabled ? 'bg-accent' : 'bg-strong'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : ''}`} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-primary-c">{title}</div>
        <div className="text-[11px] text-muted-c mt-0.5">{description}</div>
      </div>
      <input
        type="time"
        value={time}
        onChange={e => onTimeChange(e.target.value)}
        disabled={!enabled}
        className={`${inputClass} w-[110px] text-center ${!enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      />
    </div>
  );
}
